/* eslint-disable sonarjs/cognitive-complexity */

import { cleanObj, getTypeof } from "@techmmunity/utils";

import type { EntityManager } from "../../../entity-manager";
import { autoGenerate } from "../helpers/auto-generate";

import { handleCustomMetadata } from "./helpers/handle-custom-metadata";
import { shouldAutoGenerate } from "./helpers/should-auto-generate";

import { MetadataUtil } from "../../../utils/metadata-util";
import { isFindOperator } from "../../../utils/operators/is-find-operator";
import { isSaveOperator } from "../../../utils/operators/is-save-operator";

import type { BaseConnectionOptions } from "../../../connection/types/connection-options";
import type { SingleSaveData } from "../../../repository/types/save-conditions";
import type { DatabaseEvents } from "../../types/database-events";
import type { CustomClass } from "../../types/metadata-type";

interface Injectables {
	entityManager: EntityManager;
	connectionOptions: BaseConnectionOptions;
}

export interface AutoGenerateEntityToDatabaseParams<Entity> {
	entity: CustomClass;
	data: SingleSaveData<Entity>;
	events: Array<DatabaseEvents>;
}

export const recursiveAutoGenerateEntityToDatabase = <Entity>(
	{ entityManager, connectionOptions }: Injectables,
	{ entity, data, events }: AutoGenerateEntityToDatabaseParams<Entity>,
) => {
	if (getTypeof(data) === "undefined") return;

	const entityMetadata = entityManager.getEntityMetadata(entity);

	const result = entityMetadata.columns.reduce((acc, columnMetadata) => {
		const key = columnMetadata.name as keyof Entity;

		const value = data[key];

		if (isFindOperator(value)) {
			/**
			 * Remove find operators
			 * (It will be cleaned below, at cleanObj(result))
			 */
			acc[key] = undefined;

			return acc;
		}

		if (isSaveOperator(value)) {
			acc[key] = value;

			return acc;
		}

		if (MetadataUtil.isCustomMetadataType(columnMetadata.type)) {
			/*
			 * ALERT: Mutability!
			 * ALERT: Recursive call!
			 */
			handleCustomMetadata({
				data: data as any,
				acc: acc as any,
				columnMetadata,
				entityManager,
				connectionOptions,
				events,
			});

			return acc;
		}

		/**
		 * Already has a value
		 */
		if (getTypeof(value) !== "undefined") {
			acc[key] = value;

			return acc;
		}

		/**
		 * Is AutoGenerated Field
		 */
		if (
			shouldAutoGenerate({
				columnMetadata,
				events,
			})
		) {
			acc[key] = autoGenerate({
				columnMetadata,
				entityMetadata,
				connectionOptions,
			});

			return acc;
		}

		return acc;
	}, data);

	return cleanObj(result) as Entity;
};

export const autoGenerateEntityToDatabase = <Entity>(
	{ entityManager, connectionOptions }: Injectables,
	{ entity, data, events }: AutoGenerateEntityToDatabaseParams<Entity>,
): Entity =>
	/**
	 * Make this way so the "return undefined" doesn't compromise the returned type
	 * (Only will return undefined if receive undefined, so it'ns an error)
	 */
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	recursiveAutoGenerateEntityToDatabase(
		{ entityManager, connectionOptions },
		{ entity, data, events },
	)!;
